import { writable } from "svelte/store";

/**
 * A writable store, but with a dirty flag. Call set/dirty with reset = true to
 * reset the dirty flag.
 *
 * Optionally can be initialized with a cacheKey to store the data in
 * localStorage. If there is a cacheKey, the initial data will be overridden by
 * the cached data if it exists.
 *
 * @param {any} initialData - Initial data
 */
export const swWritable = (initialData, opts = {}) => {
  // Very important that we copy the object that we receive/pass along
  // to subscribers as otherwise they could mutate the object inside
  // the store nuking our checks for equality and dirty state.  For
  // most cases, JSON.stringify is sufficient, but for more complex
  // objects developers can provide their own copy and isEqual
  // methods.
  const {
    isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b),
    copy = (a) => JSON.parse(JSON.stringify(a)),
    cacheKey = null,
  } = opts;

  let data = initialData;
  if (cacheKey) {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      data = JSON.parse(cached);
    }
  }
  const dirty = writable(false);

  const subscribers = new Set();
  const subscribe = (subscriber) => {
    subscribers.add(subscriber);
    const value = copy(data);
    subscriber(value);
    return () => {
      subscribers.delete(subscriber);
    };
  };

  const notify = () => {
    // Copy is necessary! See comment at top.
    const value = copy(data);
    subscribers.forEach((s) => s(value));
  };

  const set = (value, reset = false) => {
    if (!reset && isEqual(data, value)) {
      return;
    }
    // Copy is necessary! See comment at top.
    data = copy(value);
    dirty.set(!reset);

    if (cacheKey) {
      localStorage.setItem(cacheKey, JSON.stringify(data));
    }

    notify();
  };

  const update = (fn, reset = false) => {
    const newData = fn(data);
    set(newData, reset);
  };

  return {
    subscribe,
    update,
    set,
    dirty: {
      // make read-only externally by only exposing subscribe
      subscribe: dirty.subscribe,
    },
  };
};
